import {useEffect, useSyncExternalStore} from "react";
import { ChatKit, useChatKit } from "@openai/chatkit-react";

const CHATKIT_API_URL = import.meta.env.VITE_CHATKIT_API_URL ?? "/chatkit";
const CHATKIT_API_DOMAIN_KEY = import.meta.env.VITE_CHATKIT_API_DOMAIN_KEY ?? "domain_pk_localhost_dev";

function useIsDark() {
    return useSyncExternalStore(
        (cb) => {
            const obs = new MutationObserver(cb);
            obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
            return () => obs.disconnect();
        },
        () => document.documentElement.classList.contains("dark"),
    );
}

type ChatKitPanelProps = {
    onShowHtml: (invocation) => void;
};
export function ChatKitPanel({ onShowHtml }: ChatKitPanelProps) {
    const theme = useIsDark() ? "dark" : "light";

    const chatkit = useChatKit({
        api: {
            url: CHATKIT_API_URL,
            domainKey: CHATKIT_API_DOMAIN_KEY,
        },
        theme: {
            colorScheme: theme,
            typography: {
                fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                baseSize: 14,
            },
            color: {
                grayscale: {
                    hue: 222,
                    tint: 5,
                    shade: theme === "dark" ? 0 : -4,
                },
                accent: {
                    primary: theme === "dark" ? "#f1f5f9" : "#1e40af",
                    level: 2,
                },
            },
            radius: "round",
            density: "normal",
        },
        threadItemActions: {
            feedback: false,
        },
        onClientTool: async (invocation) => {
            console.log("invocation", invocation);
            if (invocation.name === "show_html") {
                onShowHtml(invocation);
                let attachment;
                try {
                    const raw = invocation.params?.attachment;
                    attachment = typeof raw === "string" ? JSON.parse(raw) : raw;
                } catch (e) {
                    console.error("Failed to parse attachment:", e);
                }
                if (attachment) {
                    const send = async () => {
                        try {
                            await fetch(attachment.preview_url).catch()
                            await chatkit.sendUserMessage({
                                attachments: [
                                    {
                                        id: attachment.id,
                                        type: attachment.type,
                                        name: attachment.name,
                                        mime_type: attachment.mime_type,
                                        preview_url: attachment.preview_url,
                                    },
                                ],
                            });
                        } catch (e) {
                            console.error("Failed to send screenshot message:", e);
                            setTimeout(send, 1000);
                        }
                    };
                    await send();
                }
                return { success: true };
            }
            return { success: false };
        }
    });

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === "html-error") {
                const text = event.data.message;
                const trySend = async () => {
                    try {
                        console.log(`[ChatKit] Sending ${text} message to assistant`);
                        await chatkit.sendUserMessage({ text })
                    } catch (e) {
                        console.error(e)
                        setTimeout(trySend, 500);
                    }
                };
                console.log("[ChatKit] Received html-error from iframe:", event.data.message);
                trySend().then();
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [chatkit]);

    return (
        <div className="relative flex-1 overflow-hidden bg-background">
            <ChatKit control={chatkit.control} className="block h-full w-full" />
        </div>
    );
}
